import React, { useState } from "react";
import { Microphone, Sparkle, Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const VOICES = [
  { id: "nova", name: "Nova", desc: "Energetic, upbeat" },
  { id: "onyx", name: "Onyx", desc: "Deep, authoritative" },
  { id: "shimmer", name: "Shimmer", desc: "Bright, cheerful" },
  { id: "fable", name: "Fable", desc: "Expressive storyteller" },
  { id: "coral", name: "Coral", desc: "Warm, friendly" },
  { id: "echo", name: "Echo", desc: "Smooth, calm" },
  { id: "alloy", name: "Alloy", desc: "Neutral, balanced" },
  { id: "sage", name: "Sage", desc: "Wise, measured" },
  { id: "ash", name: "Ash", desc: "Clear, articulate" },
];

export default function VoiceoverStudio() {
  const { setCredits } = useAuth();
  const [text, setText] = useState("");
  const [voice, setVoice] = useState("nova");
  const [quality, setQuality] = useState("tts-1");
  const [loading, setLoading] = useState(false);
  const [audio, setAudio] = useState(null);

  const submit = async () => {
    if (!text.trim()) { toast.error("Enter script text"); return; }
    if (text.length > 4000) { toast.error("Max 4000 chars"); return; }
    setLoading(true); setAudio(null);
    try {
      const { data } = await api.post("/ai/voiceover", { text, voice, model: quality });
      if (typeof data?._credits_remaining === "number") setCredits(data._credits_remaining);
      setAudio(`data:audio/mp3;base64,${data.audio_base64}`);
      toast.success("Voiceover ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = audio; a.download = `clipforge-voiceover-${voice}.mp3`; a.click();
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="voiceover-studio">
      <p className="text-[#FF3B6B] text-sm font-mono">// AI VOICEOVERS</p>
      <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Pro-grade voiceovers in 5s</h1>
      <p className="text-white/50 mt-1">9 voices · OpenAI TTS · MP3 download</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 glass rounded-xl p-6 space-y-5">
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Script text</label>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} maxLength={4000} placeholder="Paste your script..." className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm focus:border-[#FF3B6B] focus:outline-none resize-none" data-testid="voiceover-text-input" />
            <p className="text-xs text-white/40 mt-1 font-mono">{text.length}/4000</p>
          </div>
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Voice</label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {VOICES.map(v => (
                <button key={v.id} onClick={() => setVoice(v.id)} className={`p-2 rounded-md text-xs transition-all text-left ${voice === v.id ? "bg-[#FF3B6B] text-white" : "bg-white/5 border border-white/10 hover:bg-white/10"}`} data-testid={`voice-${v.id}`}>
                  <div className="font-semibold">{v.name}</div>
                  <div className="text-[10px] opacity-70">{v.desc}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Quality</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button onClick={() => setQuality("tts-1")} className={`py-2 rounded-md text-sm ${quality === "tts-1" ? "bg-[#FF3B6B]" : "bg-white/5 border border-white/10"}`} data-testid="quality-standard">Standard</button>
              <button onClick={() => setQuality("tts-1-hd")} className={`py-2 rounded-md text-sm ${quality === "tts-1-hd" ? "bg-[#FF3B6B]" : "bg-white/5 border border-white/10"}`} data-testid="quality-hd">HD</button>
            </div>
          </div>
          <button onClick={submit} disabled={loading} className="w-full btn-primary py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="generate-voiceover-btn">
            {loading ? "Generating..." : <><Sparkle weight="fill" size={16} /> Generate voiceover</>}
          </button>
        </div>

        <div className="lg:col-span-7">
          {!audio && !loading && (
            <div className="glass rounded-xl p-12 text-center text-white/40 border-dashed h-full flex flex-col items-center justify-center">
              <Microphone size={50} className="mb-3 text-white/20" />
              <p>Your voiceover audio will appear here</p>
            </div>
          )}
          {loading && <div className="glass rounded-xl p-12 text-center text-white/60">Generating voiceover...</div>}
          {audio && (
            <div className="glass rounded-xl p-6 space-y-4" data-testid="voiceover-result">
              <audio controls src={audio} className="w-full" data-testid="voiceover-audio" />
              <button onClick={download} className="btn-primary px-5 py-2.5 rounded-lg font-medium flex items-center gap-2" data-testid="download-voiceover-btn">
                <Download size={16} /> Download MP3
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
